import { RoutePaths } from "enums/RoutePaths";
import { signOut } from "firebase/auth";
import useFetch from "hooks/useFetch";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  TextField,
  styles as ui,
  useConfirm,
} from "stories/Admin";
import { auth } from "../../firebase";
import PageModal from "../SuperAdminPages/LocationsPage/PageModal";
import PartnerKeyCard from "./PartnerKeyCard";
import { PartnerKey, PartnerOrg, partnerStatusLabel } from "./partnerTypes";
import styles from "./PartnerPage.module.scss";

/*
 * For partnerens admins (f.eks. en forening): lag en widget-nøkkel per nettside, lim inn koden,
 * og se hvor mye widgeten er brukt og om siden krediterer iBønn. Superadmin kan også åpne siden.
 */

const PartnerPage: React.FC = () => {
  const navigate = useNavigate();
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const lastErrorRef = useRef(lastError);
  lastErrorRef.current = lastError;
  const confirm = useConfirm();

  const [orgs, setOrgs] = useState<PartnerOrg[] | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [notice, setNotice] = useState<{
    tone: "ok" | "error" | "warn";
    text: string;
  } | null>(null);

  // Ny nøkkel / endre side
  const [form, setForm] = useState<{ orgId: number; key?: PartnerKey } | null>(
    null,
  );
  const [name, setName] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await executeRef.current("/api/v1/me/partners", "GET");
    if (Array.isArray(result)) {
      setOrgs(result);
      setFailed(false);
    } else {
      setOrgs([]);
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await auth.authStateReady();
      const user = auth.currentUser;
      if (!user || user.isAnonymous) {
        navigate(RoutePaths.Login, { replace: true });
        return;
      }
      if (!active) return;
      setEmail(user.email);
      load();
    })();
    return () => {
      active = false;
    };
  }, [load, navigate]);

  const logout = async () => {
    await signOut(auth).catch(() => undefined);
    sessionStorage.clear();
    navigate(RoutePaths.Login, { replace: true });
  };

  const openCreate = (orgId: number) => {
    setForm({ orgId });
    setName("");
    setPageUrl("");
    setFormError(null);
  };

  const openEdit = (orgId: number, key: PartnerKey) => {
    setForm({ orgId, key });
    setName(key.name);
    setPageUrl(key.pageUrl);
    setFormError(null);
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setFormError(null);
    const body = { name: name.trim() || undefined, pageUrl: pageUrl.trim() };
    const result = form.key
      ? await executeRef.current(
          `/api/v1/partners/${form.orgId}/keys/${form.key.id}`,
          "PATCH",
          body,
        )
      : await executeRef.current(
          `/api/v1/partners/${form.orgId}/keys`,
          "POST",
          body,
        );
    setSaving(false);
    if (result) {
      setForm(null);
      setNotice({
        tone: "ok",
        text: form.key
          ? "Lagret."
          : "Nøkkelen er laget. Kopier koden og lim den inn på siden, og trykk «Sjekk nå».",
      });
      load();
    } else {
      setFormError(
        lastErrorRef.current()?.message || "Kunne ikke lagre. Prøv igjen.",
      );
    }
  };

  const check = async (orgId: number, key: PartnerKey) => {
    setCheckingId(key.id);
    setNotice(null);
    const result = await executeRef.current(
      `/api/v1/partners/${orgId}/keys/${key.id}/check`,
      "POST",
    );
    setCheckingId(null);
    if (result) {
      setNotice({
        tone: result.result === "OK" ? "ok" : "warn",
        text:
          result.result === "OK"
            ? "Siden krediterer iBønn. Widgeten er aktiv."
            : result.message || "Sjekken fant feil.",
      });
    } else {
      setNotice({
        tone: "error",
        text: lastErrorRef.current()?.message || "Kunne ikke sjekke siden nå.",
      });
    }
    load();
  };

  const revoke = async (orgId: number, key: PartnerKey) => {
    const ok = await confirm({
      title: `Trekke tilbake «${key.name}»?`,
      message: `Widgeten på ${key.domain} slutter å virke med en gang. Dette kan ikke angres.`,
      confirmLabel: "Trekk tilbake",
      tone: "danger",
    });
    if (!ok) return;
    await executeRef.current(
      `/api/v1/partners/${orgId}/keys/${key.id}`,
      "DELETE",
    );
    load();
  };

  return (
    <div className={`${ui.root} ${styles.page}`}>
      <header className={styles.topbar}>
        <span className={styles.brand}>
          <img
            src="/logo192.png"
            alt=""
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          iBønn · Partner
        </span>
        <span className={styles.topRight}>
          {email && <span className={styles.muted}>{email}</span>}
          <Button
            size="sm"
            variant="secondary"
            icon={<Icon name="logout" size={14} />}
            onClick={logout}
          >
            Logg ut
          </Button>
        </span>
      </header>

      <main className={styles.main}>
        <PageHeader
          title="Bønnetider på nettsiden deres"
          subtitle="Vis dagens bønnetider, jamat og jummah fra iBønn på nettsiden. Besøkende velger selv moské. Én nøkkel gjelder ett nettsted."
        />

        <Card title="Slik fungerer det">
          <ol className={styles.steps}>
            <li>Lag en nøkkel med adressen til siden widgeten skal stå på.</li>
            <li>
              Kopier koden og lim den inn i HTML-en på siden. Koden har både
              widgeten og lenken til iBønn.
            </li>
            <li>Trykk «Sjekk nå». iBønn sjekker siden hver natt.</li>
            <li>
              Fjernes widgeten eller lenken til iBønn (ibønn.no, App Store eller
              Google Play), stopper widgeten med en gang. Rett opp og trykk
              «Sjekk nå» for å starte den igjen.
            </li>
          </ol>
        </Card>

        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}

        {orgs === null ? (
          <div className={styles.skeletons}>
            {[0, 1].map((i) => (
              <Skeleton key={i} height={120} />
            ))}
          </div>
        ) : failed ? (
          <Alert tone="error">
            Kunne ikke hente partnerdata. Last inn siden på nytt.
          </Alert>
        ) : orgs.length === 0 ? (
          <EmptyState icon="key" title="Du er ikke admin for noen partner">
            Ta kontakt med iBønn hvis dere vil vise bønnetidene på nettsiden
            deres.
          </EmptyState>
        ) : (
          orgs.map((org) => {
            const activeKeys = org.keys.filter((k) => !k.revokedAt);
            const canCreate =
              org.status === "ACTIVE" && activeKeys.length < org.maxKeys;
            return (
              <section key={org.id} className={styles.org}>
                <div className={styles.orgHead}>
                  <h2 className={styles.h2}>{org.name}</h2>
                  <span className={styles.muted}>
                    {partnerStatusLabel[org.status]} · {activeKeys.length} av{" "}
                    {org.maxKeys} nøkler i bruk
                  </span>
                  <Button
                    icon={<Icon name="key" size={16} />}
                    disabled={!canCreate}
                    onClick={() => openCreate(org.id)}
                  >
                    Ny nøkkel
                  </Button>
                </div>
                {org.status !== "ACTIVE" && (
                  <Alert tone="warn">
                    Organisasjonen er{" "}
                    {partnerStatusLabel[org.status].toLowerCase()}. Ta kontakt
                    med iBønn.
                  </Alert>
                )}
                {org.keys.length === 0 ? (
                  <EmptyState icon="key" title="Ingen nøkler ennå">
                    Lag en nøkkel for nettsiden deres.
                  </EmptyState>
                ) : (
                  org.keys.map((k) => (
                    <PartnerKeyCard
                      key={k.id}
                      apiKey={k}
                      checking={checkingId === k.id}
                      onCheck={(key) => check(org.id, key)}
                      onEdit={(key) => openEdit(org.id, key)}
                      onRevoke={(key) => revoke(org.id, key)}
                    />
                  ))
                )}
              </section>
            );
          })
        )}
      </main>

      <PageModal
        open={!!form}
        title={form?.key ? "Endre side" : "Ny nøkkel"}
        icon="key"
        width={560}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setForm(null)}>
              Avbryt
            </Button>
            <Button onClick={save} loading={saving} disabled={!pageUrl.trim()}>
              {form?.key ? "Lagre" : "Lag nøkkel"}
            </Button>
          </>
        }
      >
        <TextField
          label="Adressen til siden widgeten skal stå på"
          placeholder="https://foreningen.no/bonnetider"
          value={pageUrl}
          onChange={(e) => setPageUrl(e.target.value)}
          help={
            form?.key
              ? `Siden må ligge på ${form.key.domain}. Et annet nettsted trenger en ny nøkkel.`
              : "Nøkkelen låses til dette nettstedet (med og uten www)."
          }
          autoFocus
        />
        <TextField
          label="Navn (valgfritt)"
          placeholder="F.eks. Nettsiden"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
        />
        {formError && <Alert tone="error">{formError}</Alert>}
      </PageModal>
    </div>
  );
};

export default PartnerPage;
